import { z } from "zod";

/**
 * Determines how a playbook is exposed to the LLM. See
 * `docs/briefs/001-playbook-type.md`.
 *
 * - `Workflow`: discoverable via `playbookDiscovery`. Workflow content is not
 *   embedded in the system prompt; only metadata is shown to the LLM.
 * - `Knowledge`: full content is embedded in the system prompt as background
 *   knowledge. Hidden from `playbookDiscovery`. Never executable.
 * - `Template`: deterministic fill target for authored email templates. Hidden
 *   from discovery and never executable.
 * - `WorkflowStep`: hidden from `playbookDiscovery` for foreground LLM (so the
 *   LLM can't auto-invoke step playbooks out of context). Invocable from any
 *   user-mediated surface (slash menu, classification, trigger config) and
 *   from a parent orchestrator that resolves children at runtime via the
 *   in-execution variant of `playbookDiscovery`.
 */
export enum PlaybookType {
  Workflow = "Workflow",
  Knowledge = "Knowledge",
  Template = "Template",
  WorkflowStep = "WorkflowStep",
}

export const PlaybookTypeSchema = z.nativeEnum(PlaybookType);

/**
 * The set of playbook types that can be executed by a worker / spawned as a
 * playbook_run. Single source of truth used by:
 * - Prisma type-filter clauses (`type: { in: INVOKABLE_PLAYBOOK_TYPES }`)
 * - server-side refusal checks (`!INVOKABLE_PLAYBOOK_TYPES.includes(type)`)
 *
 * `Knowledge` and `Template` are intentionally excluded. Knowledge playbooks are
 * embedded directly in the system prompt as context; Template playbooks are
 * deterministic fill targets. Neither has execution semantics.
 *
 * `Workflow` and `WorkflowStep` are both included. Foreground discovery
 * (`playbookDiscovery` for the LLM) applies its own narrower filter to keep
 * `WorkflowStep` UUIDs out of the LLM's tool-discovery output; that defense
 * is separate from this constant.
 */
export const INVOKABLE_PLAYBOOK_TYPES = [PlaybookType.Workflow, PlaybookType.WorkflowStep] as const;

/**
 * Type-guard / predicate form of {@link INVOKABLE_PLAYBOOK_TYPES} for inline
 * checks. Prefer this over comparing to individual non-invokable types directly
 * so the executable surface has one centralized definition.
 */
export function isInvokablePlaybookType(type: PlaybookType | string): boolean {
  return (INVOKABLE_PLAYBOOK_TYPES as readonly string[]).includes(type);
}

/**
 * The set of playbook types `playbookReader` will return for a known id.
 * Deliberately different from {@link INVOKABLE_PLAYBOOK_TYPES}: reading is not
 * executing. `Knowledge` is readable, since handing back authored reference
 * material by a known id is the whole point of the reader. `Template` and
 * `WorkflowStep` are excluded: they are reached only through their own
 * deterministic fill / execution paths, not by a direct read.
 */
export const READABLE_PLAYBOOK_TYPES = [PlaybookType.Workflow, PlaybookType.Knowledge] as const;

/** Type-guard / predicate form of {@link READABLE_PLAYBOOK_TYPES}. */
export function isReadablePlaybookType(type: PlaybookType | string): boolean {
  return (READABLE_PLAYBOOK_TYPES as readonly string[]).includes(type);
}
